import { lazy, Suspense } from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { COMMERCIAL_PRICING } from '@connexio/shared/constants';
import { useLocale } from '@/hooks/useLocale';
import { formatNumber } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { ContactEmailNote } from './ContactEmailNote';
import { PrimaryCta } from './PrimaryCta';
import { MARKETING_INLINE_LINK_CLASS, Section } from './Section';
import styles from './Hero.module.css';

// The product visual sits below the hero copy. Loading it as its own chunk keeps the
// showcase registry (and its module icons) out of the chunks `/` needs for first paint.
const ShowcaseVisual = lazy(() =>
  import('@/features/showcase/components/ShowcaseVisual').then((m) => ({
    default: m.ShowcaseVisual,
  })),
);

/** `trial` is only shown while self-service sign-up is open. */
const POINT_KEYS = ['price', 'trial', 'swedish', 'eu'] as const;

interface HeroProps {
  signupEnabled: boolean;
}

export function Hero({ signupEnabled }: HeroProps) {
  const { t } = useTranslation('marketing');
  const { locale } = useLocale();
  const points = POINT_KEYS.filter((key) => signupEnabled || key !== 'trial');
  const pointValues = {
    amount: formatNumber(COMMERCIAL_PRICING.amountExVatSek, locale),
    days: COMMERCIAL_PRICING.trialDays,
  };

  return (
    // `overflow-x-clip`, not `overflow-hidden`: the glow behind the product visual
    // overhangs the column and would otherwise scroll the whole page sideways, but
    // clipping both axes would also cut the visual's deliberate bleed into the next band.
    <Section
      tone="hero"
      labelledBy="marketing-hero-title"
      className={styles.section}
    >
      <div className={styles.box}>
        {/* Plain eyebrow like every other section heading: the old pill was the
            page's only round shape outside badges. */}
        <p className={styles.eyebrow}>
          {t('hero.eyebrow')}
        </p>

        <h1
          id="marketing-hero-title"
          className={styles.title}
        >
          {t('hero.title')}
        </h1>

        <p className={styles.subtitle}>
          {t('hero.subtitle')}
        </p>

        <div className={styles.stack}>
          <PrimaryCta signupEnabled={signupEnabled} />
          <Button asChild variant="outline" size="xl">
            <a href="#pricing">{t('hero.seePrice')}</a>
          </Button>
        </div>

        {signupEnabled ? null : <ContactEmailNote className={styles.contactEmailNote} />}

        <p className={cn(styles.text, 'text-body-m')}>
          {t('hero.signInPrompt')}{' '}
          <Link to="/login" className={MARKETING_INLINE_LINK_CLASS}>
            {t('hero.signIn')}
          </Link>
        </p>
      </div>

      {/* Wider than the copy column so the points sit on one row at lg instead of
          stacking into a ragged centred list. */}
      <ul
        className={cn(
          styles.list,
          points.length === 4 ? styles.list2 : styles.list3,
        )}
      >
        {/* Centred per column only once the points sit side by side; stacked in one
            column, centring each item gives every line a different left edge. */}
        {points.map((key) => (
          <li key={key} className={styles.item}>
            <Check className={styles.icon} aria-hidden="true" />
            <span className={styles.text2}>
              {t(`hero.points.${key}`, pointValues)}
            </span>
          </li>
        ))}
      </ul>

      {/* Bleeds into the section below: the visual is cropped by the next band, which
          reads as depth rather than as a screenshot sitting in its own empty box. */}
      <div className={styles.box2}>
        <div className={cn(styles.box3, 'marketing-visual-glow')}>
          <Suspense fallback={<div aria-hidden="true" className={styles.box4} />}>
            <ShowcaseVisual moduleId="deals" />
          </Suspense>
        </div>
      </div>
    </Section>
  );
}
